import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AxiosResponse } from 'axios';
import axiosInstance from '../../Axios';
import { redirectToApplication } from '../../services/AuthService';
import Loader from '../../components/loader/Loader';

/** One in-flight exchange per OTT so React Strict Mode / remounts do not POST twice. */
const exchangeOttInFlight = new Map<string, Promise<AxiosResponse>>();

const Switch: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ott = searchParams.get('ott');
    const application = searchParams.get('application');

    if (!ott || !application) {
      setError('Invalid switch request. Missing required parameters.');
      return;
    }

    const run = async (response: AxiosResponse) => {
      // The exchange establishes the SSO frontend session only — no app token
      // is returned. The handoff back to the app uses a fresh login OTT via
      // the normal /product/get-token flow.
      const payload = response.data?.data ?? response.data;
      if (!payload.ssoToken) {
        setError('Invalid response from server. Please try again.');
        return;
      }
      localStorage.setItem('token', payload.ssoToken);
      if (payload.user) {
        localStorage.setItem('user', JSON.stringify(payload.user));
      }
      const redirected = await redirectToApplication(application);
      if (!redirected) {
        setError('Could not redirect to the application. Please try again.');
      }
    };

    let p = exchangeOttInFlight.get(ott);
    if (!p) {
      p = axiosInstance.post('/auth/exchange-ott', { ott, application });
      exchangeOttInFlight.set(ott, p);
      p.finally(() => exchangeOttInFlight.delete(ott));
    }

    p.then(run).catch(() => {
      setError('Switch link has expired or is invalid. Please try again.');
    });
  }, [searchParams]);

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 flex-column gap-3">
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  return <Loader />;
};

export default Switch;
